import { formatBytes, formatDate, formatPhone, normalizePhone, type Tenant } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

/** Super admin: boshqa elektrik firmalar (alohida sahifa). Ularning obyekt/mijozlarini KO'RMAYDI — faqat xotira hajmini. */
export function TenantsPage() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [toggling, setToggling] = useState<Tenant | null>(null);

  const list = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => unwrap(await api.GET("/api/tenants/")),
  });
  const rows = list.data ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ["tenants"] });
  const totalBytes = rows.reduce((n, r) => n + (r.usage?.total ?? 0), 0);

  const toggle = useMutation({
    mutationFn: async (tn: Tenant) =>
      unwrap(
        await api.PATCH("/api/tenants/{id}/", {
          params: { path: { id: tn.id } },
          body: { is_active: !tn.is_active },
        }),
      ),
    onSuccess: () => {
      setToggling(null);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <>
      <PageHeader
        title={t("tenants.title")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> {t("tenants.add")}
          </Button>
        }
      />
      <p className="mb-4 text-sm text-muted-foreground">{t("tenants.hint")}</p>

      {list.isLoading && <Spinner className="m-8 size-8" />}
      {list.data && rows.length === 0 && <EmptyState>{t("tenants.empty")}</EmptyState>}
      {rows.length > 0 && (
        <p className="mb-3 text-sm" data-testid="tenants-total">
          {t("tenants.total_storage", { size: formatBytes(totalBytes), n: rows.length })}
        </p>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((tn) => {
          const share = totalBytes ? Math.round(((tn.usage?.total ?? 0) / totalBytes) * 100) : 0;
          return (
            <Card key={tn.id} data-testid={`tenant-${tn.id}`} className="flex flex-col gap-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-lg font-bold">{tn.name}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(tn.created_at)}</div>
                </div>
                <Badge tone={tn.is_active ? "success" : "danger"}>
                  {tn.is_active ? t("tenants.active") : t("tenants.stopped")}
                </Badge>
              </div>
              {tn.admin && (
                <div className="text-sm">
                  <div className="font-medium">{tn.admin.full_name || t("tenants.admin")}</div>
                  <div className="text-muted-foreground">
                    {formatPhone(tn.admin.phone)}
                    {tn.admin.email ? ` · ${tn.admin.email}` : ""}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <Stat label={t("tenants.electricians")} value={tn.counts?.electricians ?? 0} />
                <Stat label={t("tenants.customers")} value={tn.counts?.customers ?? 0} />
                <Stat label={t("tenants.properties")} value={tn.counts?.properties ?? 0} />
              </div>
              <div>
                <div className="mb-1 flex justify-between text-sm">
                  <span>{t("tenants.storage")}</span>
                  <span className="font-semibold">{formatBytes(tn.usage?.total ?? 0)}</span>
                </div>
                <div
                  role="progressbar"
                  aria-label={t("tenants.storage")}
                  aria-valuenow={share}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 overflow-hidden rounded-full bg-muted"
                >
                  <div className="h-full rounded-full bg-primary" style={{ width: `${share}%` }} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(tn)}>
                  <KeyRound className="size-4" /> {t("tenants.edit_admin")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setToggling(tn)}>
                  {tn.is_active ? t("tenants.stop") : t("tenants.resume")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <TenantForm
        key={`new-${creating}`}
        open={creating}
        onOpenChange={setCreating}
        onSaved={refresh}
      />
      <AdminForm
        key={editing?.id ?? "none"}
        tenant={editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={refresh}
      />
      <ConfirmDialog
        open={!!toggling}
        onOpenChange={(o) => !o && setToggling(null)}
        title={toggling?.is_active ? t("tenants.stop_title") : t("tenants.resume_title")}
        message={
          toggling?.is_active
            ? t("tenants.stop_message", { name: toggling?.name ?? "" })
            : t("tenants.resume_message", { name: toggling?.name ?? "" })
        }
        confirmLabel={toggling?.is_active ? t("tenants.stop") : t("tenants.resume")}
        danger={!!toggling?.is_active}
        pending={toggle.isPending}
        onConfirm={() => toggling && toggle.mutate(toggling)}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-muted p-2">
      <div className="text-lg font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function fieldError(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.body && typeof e.body === "object") {
    const b = e.body as Record<string, unknown>;
    const parts = Object.entries(b)
      .filter(([k]) => k !== "code")
      .map(([, v]) => (Array.isArray(v) ? v.join(" ") : String(v)));
    if (parts.length) return parts.join(" ");
  }
  return errorMessage(e, fallback);
}

function TenantForm({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const normalized = normalizePhone(phone);
      if (!normalized) throw new Error("phone");
      return unwrap(
        await api.POST("/api/tenants/", {
          body: {
            company_name: company.trim(),
            full_name: name.trim(),
            phone: normalized,
            email: email.trim(),
            password,
          },
        }),
      );
    },
    onSuccess: () => {
      toast.success(t("tenants.created"));
      onOpenChange(false);
      onSaved();
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "phone"
          ? t("auth.phone_invalid")
          : fieldError(e, t("common.error")),
      ),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!company.trim()) return setError(t("tenants.company_required"));
    if (password.length < 8) return setError(t("users.password_short"));
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("tenants.add")}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("tenants.company_name")}>
          {(id) => <Input id={id} value={company} onChange={(e) => setCompany(e.target.value)} />}
        </Field>
        <Field label={t("users.full_name")}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t("tenants.login_phone")} hint={t("users.login_hint")}>
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              placeholder="90 123 45 67"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("users.email")} hint={t("users.email_hint")}>
          {(id) => (
            <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <Field label={t("auth.password")} hint={t("tenants.password_hint")}>
          {(id) => (
            <Input
              id={id}
              type="text"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {t("tenants.create")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function AdminForm({
  tenant,
  onOpenChange,
  onSaved,
}: {
  tenant: Tenant | null;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const a = tenant?.admin;
  const [phone, setPhone] = useState(a?.phone ?? "");
  const [name, setName] = useState(a?.full_name ?? "");
  const [email, setEmail] = useState(a?.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const normalized = normalizePhone(phone);
      if (!normalized) throw new Error("phone");
      return unwrap(
        await api.PATCH("/api/tenants/{id}/admin/", {
          params: { path: { id: tenant!.id } },
          body: {
            full_name: name.trim(),
            phone: normalized,
            email: email.trim(),
            ...(password ? { password } : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      onOpenChange(false);
      onSaved();
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "phone"
          ? t("auth.phone_invalid")
          : fieldError(e, t("common.error")),
      ),
  });

  return (
    <Dialog
      open={!!tenant}
      onOpenChange={onOpenChange}
      title={t("tenants.edit_admin")}
      description={tenant?.name}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (password && password.length < 8) return setError(t("users.password_short"));
          save.mutate();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t("users.full_name")}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t("tenants.login_phone")} hint={t("users.login_hint")}>
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("users.email")}>
          {(id) => (
            <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <Field label={t("users.password_new")} hint={t("tenants.admin_password_hint")}>
          {(id) => (
            <Input
              id={id}
              type="text"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
